/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Button,
  Image,
  Spinner,
  ButtonGroup,
  Accordion,
  AccordionItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@nextui-org/react';
import { TaskOffers } from 'common/src/utils/types';
import { toast } from 'react-toastify';
import { FaWhatsapp, FaPhone, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'wouter';
import AvailabilityCalendar from '../../../pages/tasker/availability/AvailabilityCalendar';
import { createEmailLog } from 'common/src/api/email_logs';
import { sendPushNotification } from 'common/src/api/services/mobile_notifications';
import { sendAskerPriceProposalEmail } from 'common/src/api/emails/askerEmails';
import {
  fetchAutomatedOfferById,
  fetchAutomatedOfferWithTask,
  fetchOneByTaskIdAutomatedOffers,
  updateAssignedAutomatedOffer,
  updateAutomatedOffer,
} from 'common/src/api/automated-offers';
import { fetchTaskersCategoriesByTaskerIds } from 'common/src/api/taskers_categories';
import { fetchOneTasker, fetchOneTaskerSelectAuthId } from 'common/src/api/taskers';
import { createAssignedTasker, deleteAssignedTasker } from 'common/src/api/assigned-taskers';
import { fetchOneTaskWithAsker } from 'common/src/api/tasks';
import { taskerBankAccounts } from 'common/src/api/tasker_bank_accounts';
import { updateTaskOffer } from 'common/src/api/task_offers';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PostulationsPage: React.FC = () => {
  const { taskId: taskIdParam } = useParams<{ taskId: string }>();
  const taskId = taskIdParam ? parseInt(taskIdParam) : null;
  const [postulations, setPostulations] = useState<TaskOffers[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState<number | null>(null);
  const [sendingProposal, setSendingProposal] = useState<number | null>(null);
  const [viewingBankAccount, setViewingBankAccount] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedBankAccount, setSelectedBankAccount] = useState<any>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (taskId) {
      loadPostulations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const loadPostulations = async () => {
    if (!taskId) {
      toast.error('ID de tarea inválido');
      return;
    }

    try {
      // Primero obtenemos el automated_task_offer_id para esta task
      const { data: automatedTaskOffer, error: automatedTaskOfferError } =
        await fetchOneByTaskIdAutomatedOffers(taskId);
      if (automatedTaskOfferError) throw automatedTaskOfferError;

      // Luego obtenemos todas las ofertas automatizadas con la información del tasker
      const { data: offersData, error: offersError } = await fetchAutomatedOfferWithTask(
        automatedTaskOffer.id
      );

      if (offersError) throw offersError;

      const taskerIds = offersData?.map((offer) => offer.tasker.id) || [];
      const { data: categoriesData, error: categoriesError } =
        await fetchTaskersCategoriesByTaskerIds(taskerIds);

      if (categoriesError) throw categoriesError;

      const postulationsWithCategories = offersData?.map((offer) => ({
        ...offer,
        tasks: offer.automated_task_offer.task,
        tasker: {
          ...offer.tasker,
          categories: categoriesData?.filter((cat) => cat.tasker_id === offer.tasker.id) || [],
        },
      }));

      setPostulations(postulationsWithCategories || []);
    } catch (error) {
      console.error('Error loading postulations:', error);
      toast.error('Error al cargar las postulaciones');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderDetails = (details: any) => {
    if (!details) return 'Sin descripción';

    return Object.entries(details).map(([key, value]) => {
      const displayValue =
        typeof value === 'object'
          ? JSON.stringify(value)
          : typeof value === 'boolean'
          ? value
            ? 'Sí'
            : 'No'
          : value;

      return (
        <div key={key} className="mt-1">
          <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>
          <span className="ml-1 text-gray-600">{String(displayValue)}</span>
        </div>
      );
    });
  };

  const handleAssign = async (taskerId: number, priceProposal: number | null) => {
    if (!taskId) return;
    setAssigning(taskerId);

    try {
      // Primero obtenemos el automated_task_offer_id para esta task
      const { data: automatedTaskOffer, error: automatedTaskOfferError } =
        await fetchAutomatedOfferById(taskId);

      if (automatedTaskOfferError) throw automatedTaskOfferError;

      // Actualizar el estado de la oferta a 'assigned'
      const { error: offerError } = await updateAssignedAutomatedOffer(
        automatedTaskOffer.id,
        taskerId
      );

      if (offerError) throw offerError;

      // Crear entrada en assigned_taskersv
      const { error: assignError } = await createAssignedTasker(taskId, taskerId);

      if (assignError) throw assignError;

      // Obtener datos necesarios para las notificaciones
      const { data: taskData, error: taskError } = await fetchOneTaskWithAsker(taskId);

      if (taskError) throw taskError;

      const { data: taskerData, error: taskerError } = await fetchOneTasker(taskerId);

      if (taskerError) throw taskerError;

      if (priceProposal) {
        // Notificar al asker sobre la propuesta de precio
        if (taskData.asker.auth_id) {
          await sendPushNotification(
            taskData.asker.auth_id,
            '¡Nueva propuesta de precio!',
            'Un Tasker ha propuesto un precio diferente para tu Task. Revisa los detalles.',
            {
              type: 'price_proposal',
              taskId: taskId,
            }
          );
        }
        toast.success('Propuesta de precio enviada al asker');
      } else {
        // Notificar al tasker sobre la asignación
        if (taskerData.auth_id) {
          await sendPushNotification(
            taskerData.auth_id,
            '¡Felicidades!',
            'Has sido asignado a un Task. ¡Bienvenido al equipo de Tasky!',
            {
              type: 'task_assigned',
              taskId: taskId,
            }
          );
        }
        toast.success('Tasker asignado correctamente');
      }

      await loadPostulations();
    } catch (error) {
      console.error('Error al asignar tasker:', error);
      toast.error('Error al asignar tasker');
    } finally {
      setAssigning(null);
    }
  };

  const handleDeassign = async (taskerId: number) => {
    if (!taskId) return;
    setAssigning(taskerId);

    try {
      // 1. Obtener el automated_task_offer_id para esta task
      const { data: automatedTaskOffer, error: automatedTaskOfferError } =
        await fetchAutomatedOfferById(taskId);

      if (automatedTaskOfferError) throw automatedTaskOfferError;

      // 2. Actualizar el estado de la oferta a 'pending'
      const { error: offerError } = await updateAutomatedOffer(automatedTaskOffer.id, taskerId);

      if (offerError) throw offerError;

      // 3. Eliminar de assigned_taskers
      const { error: assignError } = await deleteAssignedTasker(taskId, taskerId);

      if (assignError) throw assignError;

      // 4. Notificar al tasker sobre la desasignación
      const { data: taskerData, error: taskerError } = await fetchOneTaskerSelectAuthId(taskerId);

      if (!taskerError && taskerData?.auth_id) {
        await sendPushNotification(
          taskerData.auth_id,
          'Actualización de Task',
          'Has sido desasignado de un Task.',
          {
            type: 'task_unassigned',
            taskId: taskId,
          }
        );
      }

      toast.success('Tasker desasignado correctamente');
      await loadPostulations();
    } catch (error) {
      console.error('Error desasignando tasker:', error);
      toast.error('Error al desasignar el tasker');
    } finally {
      setAssigning(null);
    }
  };

  const handleViewBankAccount = async (taskerId: number) => {
    try {
      setViewingBankAccount(taskerId);
      const { data: bankData, error } = await taskerBankAccounts(taskerId)

      if (error) throw error;
      setSelectedBankAccount(bankData);
      onOpen();
    } catch (error) {
      console.error('Error fetching bank account:', error);
      setSelectedBankAccount(null);
      onOpen();
    } finally {
      setViewingBankAccount(null);
    }
  };

  const handleSendProposal = async (taskerId: number, priceProposal: number) => {
    if (!taskId) return;
    setSendingProposal(taskerId);

    try {
      // Actualizar task_offers para marcar la propuesta como enviada
      const { error: updateError } = await updateTaskOffer(taskId, taskerId)

      if (updateError) throw updateError;

      // Resto del código existente para enviar el email
      const { data: taskData, error: taskError } = await fetchOneTaskWithAsker(taskId)

      if (taskError) throw taskError;

      const { data: taskerData, error: taskerError } = await fetchOneTasker(taskerId)

      if (taskerError) throw taskerError;

      await sendAskerPriceProposalEmail({
        task: taskData,
        tasker: taskerData,
        originalPrice: taskData.price,
        proposedPrice: priceProposal,
      });

      // Add email log creation
      await createEmailLog({
        recipient_email: taskData.asker.email,
        sender_email: 'soporte@taskyapp.cl',
        label: 'price-proposal',
        status: 'success',
        task_id: taskId,
      });

      // Enviar notificación push al asker
      if (taskData.asker.auth_id) {
        await sendPushNotification(
          taskData.asker.auth_id,
          '¡Nueva propuesta de precio!',
          'Un Tasker ha propuesto un precio diferente para tu Task. Revisa los detalles.',
          {
            type: 'price_proposal',
            taskId: taskId,
          }
        );
      }

      toast.success('Propuesta de precio enviada al asker');
      await loadPostulations(); // Reload the postulations after successful update
    } catch (error) {
      console.error('Error enviando propuesta:', error);
      toast.error('Error al enviar la propuesta');
    } finally {
      setSendingProposal(null);
    }
  };

  if (loading) {
    return <Spinner label="Cargando postulaciones..." />;
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Postulaciones ({postulations.length})</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {postulations.map((postulation) => (
          <Card key={postulation.id} className="mb-4">
            <CardBody>
              {/* Cabecera con foto y nombre */}
              <div className="flex items-center gap-3 mb-3">
                <Image
                  src={postulation.tasker.photo_selfie_url || '/default-avatar.png'}
                  alt={`${postulation.tasker.name} ${postulation.tasker.last_name}`}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <Link href={`/tasker/${postulation.tasker.id}`}>
                    <a className="text-md font-semibold hover:text-purple-600">
                      {postulation.tasker.name} {postulation.tasker.last_name}
                    </a>
                  </Link>
                  <p className="text-sm text-gray-500">{postulation.tasker.university}</p>
                  <p className="text-sm text-gray-500">{postulation.tasker.career}</p>
                  <p className="text-sm text-gray-500">Año: {postulation.tasker.university_year}</p>
                </div>
              </div>

              {/* Acordeón de Categorías */}
              <Accordion className="mb-3 px-0">
                <AccordionItem
                  key="categories"
                  aria-label="Categorías y Experiencia"
                  title="Categorías y Experiencia"
                  className="text-sm"
                >
                  <div className="space-y-2">
                    {postulation.tasker.categories?.map((category: any) => (
                      <div key={category.id} className="p-2 rounded-lg bg-purple-50">
                        <h4 className="font-semibold text-purple-700">
                          {category.categories.name}
                        </h4>
                        <div className="text-sm mt-1">{renderDetails(category.details)}</div>
                      </div>
                    ))}
                  </div>
                </AccordionItem>
                <AccordionItem
                  key="availability"
                  aria-label="Disponibilidad"
                  title="Disponibilidad"
                  className="text-sm"
                >
                  {postulation.tasker.availability ? (
                    <AvailabilityCalendar
                      availability={postulation.tasker.availability as number[][]}
                    />
                  ) : (
                    <p className="text-gray-500">No hay información de disponibilidad</p>
                  )}
                </AccordionItem>
              </Accordion>

              {/* Certificados */}
              <div className="flex flex-wrap gap-2 mb-3">
                {postulation.tasker.certificate_regular_alumn_url && (
                  <Button
                    size="sm"
                    variant="flat"
                    color="primary"
                    as="a"
                    href={postulation.tasker.certificate_regular_alumn_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startContent={<FaDownload size={14} />}
                  >
                    Alumno Regular
                  </Button>
                )}
                {postulation.tasker.certificate_criminal_record_url && (
                  <Button
                    size="sm"
                    variant="flat"
                    color="primary"
                    as="a"
                    href={postulation.tasker.certificate_criminal_record_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startContent={<FaDownload size={14} />}
                  >
                    Antecedentes
                  </Button>
                )}
              </div>

              {/* Chips de información
              <div className="flex flex-wrap gap-1 mb-3">
                {postulation.tasker.own_movilization && (
                  <Chip size="sm" variant="flat" color="success">Movilización</Chip>
                )}
                {postulation.tasker.only_tasks_online && (
                  <Chip size="sm" variant="flat" color="warning">Online</Chip>
                )}
              </div> */}

              {/* Check if the task_offer is assigned */}
              {postulation.status === 'assigned' ? (
                <div className="mb-3 p-4 bg-green-100 rounded-lg shadow-md">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-green-800 mb-2">Tasker aceptado</h3>
                      <p className="text-sm text-green-700">
                        Este Tasker ha sido asignado a la tarea.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      onClick={() => handleDeassign(postulation.tasker.id)}
                      isLoading={assigning === postulation.tasker.id}
                    >
                      Desasignar Tasker
                    </Button>
                  </div>
                </div>
              ) : (
                // Existing UI for price proposal or other statuses
                postulation.price_proposal && (
                  <div className="mb-3 p-4 bg-yellow-100 rounded-lg shadow-md">
                    <h3 className="text-lg font-bold text-yellow-800 mb-2">Propuesta de Precio</h3>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm">
                          <span className="font-semibold">Precio original:</span>{' '}
                          <span className="line-through text-gray-500">
                            ${postulation.tasks.price.toLocaleString('es-CL')}
                          </span>
                        </p>
                        <p className="text-sm">
                          <span className="font-semibold">Precio propuesto:</span>{' '}
                          <span className="text-yellow-800">
                            ${Math.round(postulation.price_proposal / 0.85).toLocaleString('es-CL')}
                            {/* ${Math.round((postulation.price_proposal / 0.8) / 0.81).toLocaleString('es-CL')} */}
                          </span>
                        </p>
                        <p className="text-xs text-gray-600">(Incluye comisión Tasky y IVA)</p>
                      </div>
                      <Button
                        size="sm"
                        color="primary"
                        className="ml-4"
                        onClick={() =>
                          handleSendProposal(postulation.tasker.id, postulation.price_proposal)
                        }
                        isLoading={sendingProposal === postulation.tasker.id}
                      >
                        Enviar propuesta a Asker
                      </Button>
                    </div>
                  </div>
                )
              )}

              {/* Action buttons */}
              <div className="flex gap-2 mt-auto">
                {!postulation.price_proposal && postulation.status !== 'assigned' && (
                  <Button
                    size="sm"
                    color="primary"
                    className="flex-1"
                    onClick={() => handleAssign(postulation.tasker.id, null)}
                    isLoading={assigning === postulation.tasker.id}
                  >
                    Asignar Tasker
                  </Button>
                )}
                <ButtonGroup size="sm">
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => handleViewBankAccount(postulation.tasker.id)}
                    isLoading={viewingBankAccount === postulation.tasker.id}
                  >
                    Ver datos bancarios
                  </Button>
                  <Button
                    as="a"
                    href={`tel:${postulation.tasker.phone_number}`}
                    variant="flat"
                    isIconOnly
                  >
                    <FaPhone />
                  </Button>
                  <Button
                    as="a"
                    href={`https://wa.me/${postulation.tasker.phone_number}`}
                    target="_blank"
                    variant="flat"
                    isIconOnly
                  >
                    <FaWhatsapp />
                  </Button>
                </ButtonGroup>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>Datos Bancarios</ModalHeader>
          <ModalBody>
            {selectedBankAccount ? (
              <div>
                <p>
                  <strong>Banco:</strong> {selectedBankAccount.bank_name}
                </p>
                <p>
                  <strong>Tipo de cuenta:</strong> {selectedBankAccount.account_type}
                </p>
                <p>
                  <strong>Número de cuenta:</strong> {selectedBankAccount.account_number}
                </p>
                <p>
                  <strong>RUT:</strong> {selectedBankAccount.rut}
                </p>
              </div>
            ) : (
              <p>No se encontraron datos bancarios para este tasker.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={onClose}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default PostulationsPage;
